import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useMediaQuery } from "react-responsive";
import { breakpoints } from "../../style/size";

import { useTranslation } from "react-i18next";
import { Tooltip as ReactTooltip } from "react-tooltip";
import Button from "../button/index";
import {
  Action,
  Bottom,
  LeftTable,
  MatchContent,
  MatchInfo,
  MatchTableWrap,
  Score,
  TeamMatches,
  TeamName,
  TeamScore,
  Top,
  RightTable,
  ScrollableContainer,
  StyledTable,
  TableButtons,
} from "../../style/MatchTable";
import Moment from "react-moment";
import { Formation } from "../Formation";
import { PageButton, PaginationWrapper } from "../../style/Favorites";

import more from "../../images/ic_more.svg";
import play from "../../images/ic_play.svg";
import playMatchBlue from "../../images/play_match_blue.svg";
import playInBall from "../../images/ic_playinball.svg";
import playMatchDarkActive from "../../images/ic_play_active.svg";
import playMatchBlueActive from "../../images/ic_play_active_blue.svg";
import playInBallBlue from "../../images/playInBall_blue.svg";
import statistics from "../../images/ic_statistics.svg";
import statisticsBlue from "../../images/statistics_blue.svg";
import formation from "../../images/ic_formation.svg";
import formationBlue from "../../images/formation_blue.svg";
import arrowDark from "../../images/arrow_dark.svg";
import arrowLight from "../../images/arrow_white.svg";

import { ReactComponent as SortAscIcon } from "../../images/icons/sortasc_icon.svg";
import { ReactComponent as SortDescIcon } from "../../images/icons/sort_desc.svg";
import { ReactComponent as SortDefaultIcon } from "../../images/icons/sort_default.svg";

import { $apiV1, $apiV2 } from "../../config/api";
import { useSelector } from "react-redux";
import { ParametersSelector } from "../ParametersSelector";
import { Pagination } from "../ui";
import { LoadingWrapper, SelectDropdown } from "../../style/Global";
import { useNotification } from "../notification/useNotification";
import { NotificationList } from "../notification/NotificationList";
import { Oval } from "react-loader-spinner";
import useOutsideClick from "../../hooks/useOutsideClick";
import { useLexicon } from "../../hooks/useLexicon";
import { useParameterLexicon } from "../../hooks/useParameterLexicon";
import { CompetitionMatchesTable } from "./CompetitionMatchesTable";
import { useSavedFilterTemplates } from "../../hooks/useSavedFilterTemplates";
import { SavedFilterTemplatesControlsView } from "../filterTemplates/SavedFilterTemplatesControls";
import { normalizeTemplateIds } from "../FilterMatches/templateFilters";

const CompetitionMatch = ({
  table = [],
  load,
  currentPage,
  totalPages,
  onPageChange,
  teamParams,
  matchesCount,
  matchPresetId = 1,
  id,
}) => {
  const l = useLexicon()
  const { getTeamParameterLexiconId } = useParameterLexicon();
  const isMobile = useMediaQuery({ query: `(${breakpoints.mobile})` });
  const { t } = useTranslation();
  const { notifications, addNotification, removeNotification } = useNotification();
  const canDownloadReport = useSelector(state => state.user.canDownloadReport);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const [openEventFilterModal, setOpenEventFilterModal] = useState(false);
  const [openRow, setOpenRow] = useState(null);
  const [tempSelectedFilters, setTempSelectedFilters] = useState({
    checkedIds: [],
    params: [],
  }); // Temporary state for filters

  const [openFormation, setOpenFormation] = useState(false);
  const [selectedFormationMatch, setSelectedFormationMatch] = useState(null);

  const [downloadFile, setDownloadFile] = useState("");

  const [tableTypeSorting, setTableTypeSorting] = useState({ type: 'default', param: '' }); // type default | asc | desc param:number

  const scrollRef1 = useRef(null);
  const scrollRef2 = useRef(null);

  useEffect(() => {
    const handleScroll = (e) => {
      if (e.target === scrollRef1.current) {
        scrollRef2.current.scrollTop = e.target.scrollTop;
      } else if (e.target === scrollRef2.current) {
        scrollRef1.current.scrollTop = e.target.scrollTop;
      }
    };
    if (scrollRef1.current && scrollRef2.current) {
      scrollRef1.current.addEventListener('scroll', handleScroll);
      scrollRef2.current.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (scrollRef1.current && scrollRef2.current) {
        scrollRef1.current.removeEventListener('scroll', handleScroll);
        scrollRef2.current.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  useEffect(() => {
    const setDefaultSelectedParams = () => {
      const checkedIds = teamParams.flatMap((group) =>
        group[1].filter((item) => item.checked).map((item) => item.id)
      );
      const checkedParams = teamParams.flatMap((group) =>
        group[1].filter((item) => item.checked)
      );
      setTempSelectedFilters({ checkedIds, params: checkedParams });
    };
    teamParams?.length > 0 && setDefaultSelectedParams();
  }, [teamParams]);

  const tableLeftNames = [
    { id: 1, name: l(991) },
    { id: 2, name: l(123) },
  ];

  const setMoreInfo = (index) => {
    setOpenRow(openRow === index ? null : index);
  };

  const resolveMatchLimit = useCallback((totalMatches) => {
    switch (Number(matchPresetId)) {
      case 2:
        return 1;
      case 3:
        return 3;
      case 4:
        return 5;
      case 5:
        return 10;
      default:
        return totalMatches;
    }
  }, [matchPresetId]);

  // Filter the table based on the selected number of matches
  const filteredTable = useMemo(() => {
    const resolvedMatchLimit = resolveMatchLimit(table.length);
    return table.slice(0, resolvedMatchLimit);
  }, [resolveMatchLimit, table]);

  const handleCloseFormation = () => {
    setOpenFormation(false);
    setSelectedFormationMatch(null);
  };

  useEffect(() => {
    // Fetch data whenever currentPage changes
  }, [currentPage]);

  const handlePrevPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const templateFilters = useMemo(() => ({
    parameterIds: normalizeTemplateIds(tempSelectedFilters?.checkedIds || []),
  }), [tempSelectedFilters?.checkedIds]);

  const handleApplyTemplate = useCallback(({ parameterIds } = {}) => {
    if (!Array.isArray(parameterIds)) {
      return;
    }

    const normalizedParameterIds = new Set(
      normalizeTemplateIds(parameterIds).map((item) => Number(item))
    );

    const nextTeamParams = (teamParams || []).map((group) => {
      const groupName = group?.[0];
      const groupParams = Array.isArray(group?.[1]) ? group[1] : [];

      return [
        groupName,
        groupParams.map((parameter) => ({
          ...parameter,
          checked: normalizedParameterIds.has(Number(parameter?.id)),
        })),
      ];
    });

    if (nextTeamParams.length === 0) {
      return;
    }

    sessionStorage.setItem("teamParameters", JSON.stringify(nextTeamParams));
    handleChangeParams(nextTeamParams);
  }, [teamParams]);

  const savedTemplates = useSavedFilterTemplates({
    pageKey: 'competition_matches',
    filters: templateFilters,
    onApplyTemplate: handleApplyTemplate,
  });

  const getParameterDisplayName = (parameterInput) => {
    const fallbackName =
      typeof parameterInput === "string"
        ? parameterInput
        : parameterInput?.parameter_name || parameterInput?.name || "";
    if (!fallbackName) return '';
    const lexiconId = getTeamParameterLexiconId(parameterInput);
    if (lexiconId) {
      return l(lexiconId) || fallbackName;
    }
    return fallbackName;
  };

  const [loadDownloadXls, setDownloadXls] = useState(false);
  const handleDownloadXls = async () => {
    setDownloadXls(true);
    const downloadNotifId = 'downloading-competition-xls';
    addNotification('Downloading...', 'info', { id: downloadNotifId, duration: 0 });
    try {
      const payload = {
        file_type: "xlsx",
        seasonId: id,
        match_ids: filteredTable.map((m) => m.id),
        match_count: filteredTable.length,
      };
      const checkedIds = tempSelectedFilters?.checkedIds;
      if (Array.isArray(checkedIds) && checkedIds.length > 0) {
        payload.params_ids = checkedIds;
      }
      const response = await $apiV1.post(`/statistics/season_match_stats_export`, payload);

      const fileUrls = response?.data?.file_url;
      console.log("fileUrlsLength", fileUrls.length);
      for (const [index, fileUrl] of fileUrls.entries()) {
        // Create a temporary <a> element
        const a = document.createElement("a");
        a.href = `${fileUrl}`;
        a.download = `file_part_${index}.xlsx`; // Specify the default filename
        a.style.display = "none"; // Hide the element
        document.body.appendChild(a);
        console.log("downloading file", index);
        a.click();
        document.body.removeChild(a); // Clean up
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setDownloadXls(false);
      removeNotification(downloadNotifId);
    }
  };

  const handleDownloadReport = async () => {
    if (!id || isDownloadingReport) return;
    const notifId = 'downloading-competition-report';
    setIsDownloadingReport(true);
    addNotification('Download in progress...', 'info', { id: notifId, duration: 0 });
    try {
      const response = await $apiV2.post(
        `/platform/season/${id}/export/report/pdf`,
        {},
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `competition-report-${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Competition report download failed', e);
      addNotification('Error, please try again later.', 'error');
    } finally {
      removeNotification(notifId);
      setIsDownloadingReport(false);
    }
  };

  const getWinner = (score, isHome) => {
    let scoreColor;
    if (isHome) {
      if (+score[0] > +score[2]) {
        scoreColor = "#36e82d";
      } else if (+score[0] === +score[2]) {
        scoreColor = "#E8D02D";
      } else {
        scoreColor = "#f20a46";
      }
    } else if (+score[2] > +score[0]) {
      scoreColor = "#36e82d";
    } else if (+score[0] === +score[2]) {
      scoreColor = "#E8D02D";
    } else {
      scoreColor = "#f20a46";
    }
    return scoreColor;
  };

  function handleChangeParams(params) {
    const checkedIds = params.flatMap((group) =>
      group[1].filter((item) => item.checked).map((item) => item.id)
    );
    const checkedParams = params.flatMap((group) =>
      group[1].filter((item) => item.checked)
    );
    setTempSelectedFilters({ checkedIds, params: checkedParams });
  }

  const handleSortTable = (param) => {
    if (tableTypeSorting.type === 'asc' && param.parameter_id === tableTypeSorting.param) {
      setTableTypeSorting({ type: 'desc', param: param.parameter_id })
    } else if (tableTypeSorting.type === 'desc' && param.parameter_id === tableTypeSorting.param) {
      setTableTypeSorting({ type: 'default', param: param.parameter_id })
    } else {
      setTableTypeSorting({ type: 'asc', param: param.parameter_id })
    }
  }

  const sortTable = (a, b) => {
    if (tableTypeSorting.param) {
      const paramA1 = a.home_team?.stats?.find(param => param.parameter_id === tableTypeSorting.param);
      const paramA2 = a.away_team?.stats?.find(param => param.parameter_id === tableTypeSorting.param);

      const paramB1 = b.home_team?.stats?.find(param => param.parameter_id === tableTypeSorting.param);
      const paramB2 = b.away_team?.stats?.find(param => param.parameter_id === tableTypeSorting.param);

      if (paramA1 && paramA2 && paramB1 && paramB2) {
        const sumA = paramA1.value + paramA2.value;
        const sumB = paramB1.value + paramB2.value;

        if (tableTypeSorting.type === 'asc') {
          return sumA - sumB;
        }
        if (tableTypeSorting.type === 'desc') {
          return sumB - sumA;
        }
      }
      return new Date(b.date) - new Date(a.date);
    }
    return new Date(b.date) - new Date(a.date);
  }

  const toolbarStyle = {
    display: "flex",
    gap: 10,
    alignSelf: "flex-end",
    marginBottom: 12,
    alignItems: "center",
    flexWrap: "wrap",
    justifyContent: "flex-end",
    width: isMobile ? "100%" : "auto",
  };

  const toolbarButtonStyle = {
    width: "fit-content",
    minWidth: isMobile ? 120 : 130,
    padding: "0 10px",
    whiteSpace: "nowrap",
    fontSize: 14,
  };

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 20 }}>
          <div style={toolbarStyle}>
            <button
              className="buttonFilter"
              onClick={() => handleDownloadXls()}
              style={toolbarButtonStyle}
            >
              {loadDownloadXls ? l(312) : l(14)}
            </button>
            <div style={{ minWidth: isMobile ? "100%" : 190, maxWidth: "100%" }}>
              <SavedFilterTemplatesControlsView
                {...savedTemplates}
                pageKey="competition_matches"
                showCreateButton={false}
                showUpdateButton={false}
                showStatusMessage={false}
                showModals={false}
              />
            </div>
            <button
              className="buttonFilter"
              onClick={() => setOpenEventFilterModal(true)}
              style={toolbarButtonStyle}
            >
              {t('smrtSearch.eventFilter')}
            </button>
            {canDownloadReport === true && (
              <Button
                onClick={handleDownloadReport}
                disabled={!id || isDownloadingReport}
                title={isDownloadingReport ? l(312) : t('statistics.downloadReport')}
                color={"secondary"}
                style={toolbarButtonStyle}
              />
            )}
          </div>
          <CompetitionMatchesTable
            data={filteredTable?.sort(sortTable)}
            loading={load}
            tableTypeSorting={tableTypeSorting}
            handleSortTable={handleSortTable}
            tempSelectedFilters={tempSelectedFilters}
            seasonId={id}
          />
        </div>
        <Pagination
          items={matchesCount}
          itemsPerPage={10}
          page={currentPage - 1 || 0}
          onPageChange={onPageChange}
        />
      <ParametersSelector
        type={"team"}
        open={openEventFilterModal}
        onClose={() => setOpenEventFilterModal(false)}
        updateData={handleChangeParams}
        savedTemplatesState={savedTemplates}
        savedTemplatesPageKey="competition_matches"
      />

      <Formation
        open={openFormation}
        handleClose={handleCloseFormation}
        match={selectedFormationMatch}
      />

      <NotificationList
        notifications={notifications}
        onClose={removeNotification}
      />
    </>
  );
};

export default CompetitionMatch;
